var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { Vue, Component, Prop, ModelSync, Watch } from 'vue-property-decorator';
import VueGtag from 'vue-gtag';
import { BRow, BCol, BFormGroup, BFormInput, BButton, BOverlay, BAlert } from 'bootstrap-vue';
import sumBy from 'lodash/sumBy';
import round from 'lodash/round';
import debounce from 'lodash/debounce';
import TabHeading from 'components/picture-frames/Tabs/TabHeading.vue';
import ReviewBreakdown from 'components/picture-frames/Tabs/ReviewOrder/ReviewBreakdown.vue';
import DiscountsBreakdown from 'components/DiscountsBreakdown.vue';
import { addToCart } from 'ts/modules/cart';
import { getApertureDimensions, getBespokeFrameCartItem, getMountWidthString } from 'ts/modules/frameDesigner';
import { validateBespokeFrame } from 'ts/modules/validation';
import { frameDelivery, sheetDelivery } from 'ts/modules/delivery';
import { checkFrameStock, checkMountStock } from 'ts/modules/stock';
import { getVatMode } from 'ts/modules/helpers';
Vue.use(VueGtag, {
    config: { id: "UA-939335-4" }
});
let ReviewOrder = class ReviewOrder extends Vue {
    constructor() {
        super(...arguments);
        this.vatMode = 'inc';
        this.loadingValidation = true;
        this.addingToCart = false;
        this.validation = {
            frame: { isValid: true, problems: [], warnings: [] },
            mount: { isValid: true, problems: [], warnings: [] },
            glazing: { isValid: true, problems: [], warnings: [] },
            backing: { isValid: true, problems: [], warnings: [] },
            undermount: { isValid: true, problems: [], warnings: [] },
            fastmount: { isValid: true, problems: [], warnings: [] }
        };
        this.deliveryValidation = {
            frame: { isValid: true, problems: [], warnings: [] },
            glazing: { isValid: true, problems: [], warnings: [] }
        };
        this.stock = {
            frame: false,
            topMount: false,
            bottomMount: false
        };
        this.loadingStock = true;
        this.debouncedCheckStock = debounce(this.checkStock, 250);
    }
    get isAddToCartDisabled() {
        if ((!this.isValid || !this.isInStock) && !this.isAdmin) {
            return true;
        }
        if (this.loadingValidation || this.loadingPrices || this.loadingStock || this.addingToCart) {
            return true;
        }
        return false;
    }
    get isValid() {
        return sumBy(Object.values(this.validation), validation => validation.problems ? validation.problems.length : 0) === 0;
    }
    get isInStock() {
        return this.stock.topMount && this.stock.bottomMount && this.stock.frame;
    }
    get isDeliveryAvailable() {
        return sumBy(Object.values(this.deliveryValidation), validation => validation.problems.length) === 0;
    }
    get apertureSize() {
        return getApertureDimensions(this.frame.size);
    }
    get mountWidth() {
        return getMountWidthString(this.frame.mount.borders);
    }
    get glassDimensions() {
        return {
            width: this.rebateDimensions.width - 3,
            length: this.rebateDimensions.length - 3
        };
    }
    get totalPrice() {
        return round(parseFloat(this.discountedPrice) * this.selectedQuantity, 2).toFixed(2);
    }
    addToCart() {
        return __awaiter(this, void 0, void 0, function* () {
            this.addingToCart = true;
            try {
                const item = getBespokeFrameCartItem(this.frame, this.selectedQuantity, this.exVatPrice, this.isDeliveryAvailable, this.imageName);
                // Log the add to cart event
                this.$gtag.event('add_to_cart', {
                    "items": [
                        {
                            "id": item.sku,
                            "name": `${item.sku} - Bespoke Frame`,
                            "brand": "Brampton Framing",
                            "category": "Bespoke Frame",
                            "quantity": this.selectedQuantity.toString(),
                            "price": this.exVatPrice
                        }
                    ]
                });
                yield addToCart(item, false);
            }
            finally {
                this.addingToCart = false;
            }
        });
    }
    validate() {
        return __awaiter(this, void 0, void 0, function* () {
            const validation = yield validateBespokeFrame(this.frame);
            this.validation.frame = validation.frame;
            this.validation.glazing = validation.glazing;
            this.validation.mount = validation.mount;
            this.validation.backing = validation.backing;
            this.validation.undermount = validation.undermount;
            this.validation.fastmount = validation.fastmount;
        });
    }
    validateDelivery() {
        var _a, _b;
        let promises = [];
        if (this.frame.moulding) {
            promises.push(frameDelivery((_a = this.frame.moulding) === null || _a === void 0 ? void 0 : _a.sku, this.rebateDimensions)
                .then(validation => this.deliveryValidation.frame = validation));
        }
        else {
            this.deliveryValidation.frame = { isValid: true, problems: [], warnings: [] };
        }
        if (this.frame.glazing) {
            promises.push(sheetDelivery(`${((_b = this.frame.glazing.category) === null || _b === void 0 ? void 0 : _b.name) == 'Acrylic Glass' ? 'A' : ''}G-${this.frame.glazing.id}`, this.glassDimensions, true).then(validation => this.deliveryValidation.glazing = validation));
        }
        else {
            this.deliveryValidation.glazing = { isValid: true, problems: [], warnings: [] };
        }
        return Promise.all(promises);
    }
    checkStock() {
        var _a, _b;
        return __awaiter(this, void 0, void 0, function* () {
            if (this.selectedQuantity < 1) {
                this.$emit('input', 1);
                return;
            }
            this.loadingStock = true;
            if (this.frame.moulding) {
                checkFrameStock(`BF-${this.frame.moulding.sku}`, this.rebateDimensions, this.selectedQuantity)
                    .then(inStock => this.stock.frame = inStock);
            }
            else {
                this.stock.frame = true;
            }
            if (this.frame.mount.type != 'none') {
                // Check the top mount
                this.stock.topMount = yield checkMountStock(`MCTS-${(_a = this.frame.mount.top) === null || _a === void 0 ? void 0 : _a.stock_lookup_sku}`, this.rebateDimensions, this.selectedQuantity);
                if (this.frame.mount.type == 'double') {
                    // Check the bottom mount stock
                    this.stock.bottomMount = yield checkMountStock(`MCTS-${(_b = this.frame.mount.top) === null || _b === void 0 ? void 0 : _b.stock_lookup_sku}`, this.rebateDimensions, this.selectedQuantity);
                }
                else {
                    this.stock.bottomMount = true;
                }
            }
            else {
                this.stock.topMount = true;
                this.stock.bottomMount = true;
            }
            this.loadingStock = false;
        });
    }
    mounted() {
        return __awaiter(this, void 0, void 0, function* () {
            // Called every time this tab is shown
            this.loadingValidation = true;
            // Get the price breakdown and validation
            yield Promise.all([
                this.validate(),
                this.validateDelivery(),
                this.checkStock()
            ]);
            this.loadingValidation = false;
        });
    }
    created() {
        this.vatMode = getVatMode();
    }
};
__decorate([
    ModelSync('quantity', 'input', { type: Number })
], ReviewOrder.prototype, "selectedQuantity", void 0);
__decorate([
    Prop(Object)
], ReviewOrder.prototype, "frame", void 0);
__decorate([
    Prop(Object)
], ReviewOrder.prototype, "rebateDimensions", void 0);
__decorate([
    Prop({ type: Boolean, default: false })
], ReviewOrder.prototype, "isAdmin", void 0);
__decorate([
    Prop({ type: String, default: null })
], ReviewOrder.prototype, "imageName", void 0);
__decorate([
    Prop({ type: String, default: '0.00' })
], ReviewOrder.prototype, "price", void 0);
__decorate([
    Prop({ type: String, default: '0.00' })
], ReviewOrder.prototype, "exVatPrice", void 0);
__decorate([
    Prop({ type: String, default: '0.00' })
], ReviewOrder.prototype, "discountedPrice", void 0);
__decorate([
    Prop({ type: Number, default: 0 })
], ReviewOrder.prototype, "discount", void 0);
__decorate([
    Prop(Boolean)
], ReviewOrder.prototype, "loadingPrices", void 0);
__decorate([
    Prop({ type: Object, default: () => ({
            frame: '0.00',
            mount: '0.00',
            glazing: '0.00',
            backing: '0.00',
            undermount: '0.00',
            fastmount: '0.00',
            fixings: '0.00',
            spacer: '0.00'
        }) })
], ReviewOrder.prototype, "prices", void 0);
__decorate([
    Watch('selectedQuantity')
], ReviewOrder.prototype, "debouncedCheckStock", void 0);
ReviewOrder = __decorate([
    Component({
        components: {
            BRow, BCol, BFormGroup, BFormInput, BButton, BOverlay, BAlert,
            TabHeading, ReviewBreakdown, DiscountsBreakdown
        }
    })
], ReviewOrder);
export default ReviewOrder;
